/**
 * aramBridgeMeshes — Murder Bridge blockout sized via aramScale (champ fixed).
 * Lane along world +Z (BL→TR under isometric camera).
 */

import { BoxGeometry, Mesh, MeshStandardMaterial, PlaneGeometry } from 'three';
import { ARAM_RELIC_PADS } from '../../domain/map/aramRelicPads.js';
import {
  ARAM_BRIDGE_WIDTH,
  ARAM_BRIDGE_LENGTH
} from '../../domain/map/aramScale.js';
import { SPAWN_PAD_CONFIG, buildSpawnPlatform } from './aramSpawnMeshes.js';

export { buildSpawnPlatform };

export const ARAM_LANE_CONFIG = {
  bridgeWidth: ARAM_BRIDGE_WIDTH,
  bridgeLength: ARAM_BRIDGE_LENGTH,
  laneAngleDeg: 45,
  voidSize: ARAM_BRIDGE_LENGTH * 1.25,
  spawnPlatformSize: SPAWN_PAD_CONFIG.size,
  spawnPlatformHeight: SPAWN_PAD_CONFIG.height,
  spawnDistance: SPAWN_PAD_CONFIG.distance,
  railHeight: 0.45,
  railThickness: ARAM_BRIDGE_WIDTH * 0.04,
  alcovePadSize: ARAM_BRIDGE_WIDTH * 0.12
};

const COLORS = {
  void: 0x0a0612,
  bridge: 0x5a4a2a,
  rail: 0x4b5563,
  alcove: 0x3d5a3d
};

export function buildVoid(THREE) {
  const PlaneGeo = THREE?.PlaneGeometry || PlaneGeometry;
  const MeshMat = THREE?.MeshStandardMaterial || MeshStandardMaterial;
  const MeshCls = THREE?.Mesh || Mesh;
  const { voidSize } = ARAM_LANE_CONFIG;
  const geo = new PlaneGeo(voidSize, voidSize);
  const mat = new MeshMat({ color: COLORS.void, roughness: 1 });
  const mesh = new MeshCls(geo, mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = -0.05;
  mesh.name = 'aram-void';
  return mesh;
}

export function buildBridge(THREE) {
  const PlaneGeo = THREE?.PlaneGeometry || PlaneGeometry;
  const MeshMat = THREE?.MeshStandardMaterial || MeshStandardMaterial;
  const MeshCls = THREE?.Mesh || Mesh;
  const { bridgeWidth: w, bridgeLength: len } = ARAM_LANE_CONFIG;
  const geo = new PlaneGeo(w, len);
  const mat = new MeshMat({ color: COLORS.bridge, roughness: 0.7 });
  const mesh = new MeshCls(geo, mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.rotation.z = 0;
  mesh.position.set(0, 0.02, 0);
  mesh.name = 'aram-bridge';
  return mesh;
}

export function buildRails(THREE) {
  const BoxGeo = THREE?.BoxGeometry || BoxGeometry;
  const MeshMat = THREE?.MeshStandardMaterial || MeshStandardMaterial;
  const MeshCls = THREE?.Mesh || Mesh;
  const { bridgeLength: len, railHeight: h, railThickness: t, bridgeWidth: w } = ARAM_LANE_CONFIG;
  const half = w / 2 + t / 2;
  const specs = [
    { x: -half, name: 'aram-rail-0' },
    { x: half, name: 'aram-rail-1' }
  ];
  return specs.map(({ x, name }) => {
    const geo = new BoxGeo(t, h, len);
    const mat = new MeshMat({ color: COLORS.rail, roughness: 0.85 });
    const mesh = new MeshCls(geo, mat);
    mesh.rotation.y = 0;
    mesh.position.set(x, h / 2, 0);
    mesh.name = name;
    return mesh;
  });
}

export function buildAlcovePads(THREE) {
  const BoxGeo = THREE?.BoxGeometry || BoxGeometry;
  const MeshMat = THREE?.MeshStandardMaterial || MeshStandardMaterial;
  const MeshCls = THREE?.Mesh || Mesh;
  const { alcovePadSize: s } = ARAM_LANE_CONFIG;
  return ARAM_RELIC_PADS.map((pad, i) => {
    const geo = new BoxGeo(s, 0.12, s);
    const mat = new MeshMat({ color: COLORS.alcove, roughness: 0.8 });
    const mesh = new MeshCls(geo, mat);
    mesh.position.set(pad.x, 0.08, pad.z);
    mesh.name = `aram-alcove-${i}`;
    return mesh;
  });
}
